import _ from 'underscore';

import { getTagListsForVizportalApi, TagsEditorState } from '@tableau/vizportalclient-common-components';

import {
  ActionHelpers,
  standardActionErrorMsg,
  standardActionSuccessMsg,
  StandardActionToastMessages
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { toNativePromise } from '~commonInfra/utils/QUtils';
import { Databases } from '~src/catalog/resources/Databases';
import { Tables } from '~src/catalog/resources/Tables';
import { Formatters, Messages } from '~src/codegen/Localize';
import { ChangeTagsDialogBody, ChangeTagsDialogBodyProps } from '~src/content/components/ChangeTagsDialogBody';
import { DataSources } from '~src/content/resources/Datasources';
import { Workbooks } from '~src/content/resources/Workbooks';
import { DataSourceErrors } from '~src/content/services/DataSourceErrors';
import { ViewErrors } from '~src/content/services/ViewErrors';
import { WorkbookErrors } from '~src/content/services/WorkbookErrors';
import { Flows } from '~src/flow/resources/Flows';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { IMetricInfo } from '~src/metrics/models/IMetricInfo';
import { Metrics } from '~src/metrics/resources/Metrics';
import { MetricErrors } from '~src/metrics/services/MetricErrors';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { CommonErrorCode, TagErrorCode } from '~src/ts/helpers/ErrorCodes';
import { TagsHelper } from '~src/ts/helpers/TagsInputHelper';
import { IContentAndLuidInfo } from '~src/ts/mappers/DatabaseAndTableMapper';
import { FlowListItemInfo } from '~src/ts/models/FlowInfo';
import { IContentInfo } from '~src/ts/models/IContentInfo';
import { IDataSourceBase } from '~src/ts/models/IDataSourceInfo';
import { IPublishedConnectionInfo } from '~src/ts/models/IPublishedConnectionInfo';
import { IViewInfo } from '~src/ts/models/IViewInfo';
import { IWorkbookBase } from '~src/ts/models/IWorkbookInfo';
import { INamedItemInfo, ITaggedItemInfo } from '~src/ts/models/resourceInfoParts';
import { Set } from '~src/ts/models/Set';
import { IChangeContentTagsResult } from '~src/ts/ServerApi/types/IChangeContentTagsAction';
import { PublishedConnectionErrors } from '~src/ts/services/PublishedConnectionErrors';
import { RestApiErrorResponse } from '~src/ts/services/RestApiService';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';
import { Views } from '~src/views/resources/Views';


export interface INamedAndTaggedItemInfo extends INamedItemInfo, ITaggedItemInfo { }

export interface IChangeTagsExecuteFnParams<T> {
  changeTagsAction: (ids: string[], addTags: string[], removeTags: string[]) => Promise<IResult>;
  getDescription: (itemSet: Set<T>) => string;
  notificationFns: StandardActionToastMessages;
  errorDetailFn: (errorItem: IErrorItem<T>) => IErrorDetail;
}

export interface IChangeTagsExecuteFnForMixedContentParams<T> {
  getDescription: (itemSet: T[]) => string;
}

export namespace ChangeTagsActionHelperService {

  export function createExecuteFn<T extends INamedAndTaggedItemInfo>(params: IChangeTagsExecuteFnParams<T>) {
    return (itemSet: Set<T>) => {
      const originalTags = TagsHelper.tagsListFromTaggedItemSet(itemSet);

      const updateTagsAndNotify = async (bodyProps: ChangeTagsDialogBodyProps, dialogState: TagsEditorState) => {
        const { tagsToAdd, tagsToRemove } = getTagListsForVizportalApi({ oldTags: originalTags, newTags: dialogState.tags });
        const result = await params.changeTagsAction(itemSet.keys(), [...tagsToAdd], [...tagsToRemove]).then(result => {
          return {
            ...result,
            tags: dialogState.tags.map(name => TagsHelper.unquote(name))
          };
        });

        ActionHelpers.displayStandardToastsForResult(
          itemSet,
          result,
          params.notificationFns,
          params.errorDetailFn
        );
        return Promise.resolve(result);
      };

      const dialogInstance = openReactActionDialog<ChangeTagsDialogBodyProps, TagsEditorState, IChangeContentTagsResult>({
        title: Messages.changeTags(),
        initialBodyProps: {
          description: params.getDescription(itemSet),
        },
        initialDialogState: {
          partialText: '',
          suggestions: [],
          tags: originalTags
        },
        bodyComponent: ChangeTagsDialogBody,
        actionName: Messages.changeTagsConfirm(),
        action: updateTagsAndNotify,
        actionDisabled: (bodyProps: ChangeTagsDialogBodyProps, dialogState: TagsEditorState) => {
          return _.isEqual(dialogState.tags, originalTags);
        },
        dismissDialogOnActionFailed: true,
        dialogWidth: TabDialogWidth.width375px
      });

      return toNativePromise(dialogInstance.result);
    };
  }

  export function createExecuteFnForMixedContent(params: IChangeTagsExecuteFnForMixedContentParams<IContentInfo>) {
    return (items: IContentInfo[]) => {
      const originalTags = TagsHelper.tagsListFromTaggedItemArray(items);

      const updateTagsAndNotify = (bodyProps: ChangeTagsDialogBodyProps, dialogState: TagsEditorState) => {
        return changeTagsForContent(items, originalTags, dialogState.tags).then((results: IResult[]) => ({
          ...ResultHelper.combineResults(results),
          tags: dialogState.tags.map(name => TagsHelper.unquote(name))
        }));
      };

      const dialogInstance = openReactActionDialog<ChangeTagsDialogBodyProps, TagsEditorState, IChangeContentTagsResult>({
        title: Messages.changeTags(),
        initialBodyProps: {
          description: params.getDescription(items),
        },
        initialDialogState: {
          partialText: '',
          suggestions: [],
          tags: originalTags
        },
        bodyComponent: ChangeTagsDialogBody,
        actionName: Messages.changeTagsConfirm(),
        action: updateTagsAndNotify,
        actionDisabled: (bodyProps: ChangeTagsDialogBodyProps, dialogState: TagsEditorState) => {
          return _.isEqual(dialogState.tags, originalTags);
        },
        dismissDialogOnActionFailed: true,
        dialogWidth: TabDialogWidth.width375px
      });

      return toNativePromise(dialogInstance.result);
    };
  }

  export function changeTagsForContent(items: IContentInfo[], oldTags: string[], newTags: readonly string[]): Promise<IResult[]> {
    // We can only change the items to a set when we split by content type because different items of different content
    // types could have the same id. A set keyed on id would remove the 'duplicate' id, even though it corresponds to
    // a different item. Ids _are_ unique to content types so making them sets here is safe.
    const views = Set.fromIdItemArray(items.filter(i => i.contentType === 'view'));
    const workbooks = Set.fromIdItemArray(items.filter(i => i.contentType === 'workbook'));
    const datasources = Set.fromIdItemArray(items.filter(i => i.contentType === 'datasource'));
    const flows = Set.fromIdItemArray(items.filter(i => i.contentType === 'flow'));
    const metrics =  Set.fromIdItemArray(items.filter(i => i.contentType === 'metric'));
    const publishedConnections =  Set.fromIdItemArray(items.filter(i => i.contentType === 'publishedConnection'));
    const databases = Set.fromIdItemArray(items.filter(i => i.contentType === 'database'));
    const tables = Set.fromIdItemArray(items.filter(i => i.contentType === 'table'));

    // NOTE: If we ever allow catalog content to be part of mixed content selection,
    // we'll need to filter by content type then ensure luid is included on the filtered collection

    const { tagsToAdd, tagsToRemove } = getTagListsForVizportalApi({ oldTags, newTags });
    const addTags = [...tagsToAdd];
    const removeTags = [...tagsToRemove];

    // Note (abrandon 2020-05-05) It appears that no code is currently using allResults. Can we remove it?
    const allResults: IResult[] = [];

    // We can't call these asynchronously because when adding a new tag, the backend creates an id for it. If
    // the calls are done at the same time, the server throws an error (NonUniqueResultException) about the
    // same tag getting two ids. Instead, we have to chain these calls and save the results from each one in an array.
    return changeTagsForWorkbooks(workbooks, addTags, removeTags)
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForFlows(flows, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForViews(views, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForDatasources(datasources, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForMetrics(metrics, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForPublishedConnections(publishedConnections, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForDatabases(databases, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return changeTagsForTables(tables, addTags, removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return allResults;
      });
  }

  function changeTagsForViews(views: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (views.size() < 1) {
      return Promise.resolve(null);
    }

    return Views.changeTags(views.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        views,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsViewsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsViewsError),
        },
        (errorItem: IErrorItem<IViewInfo>) => ViewErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForFlows(flows: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (flows.size() < 1) {
      return Promise.resolve(null);
    }

    return Flows.changeTags(flows.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        flows,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsFlowsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsFlowsError),
        },
        (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForWorkbooks(workbooks: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (workbooks.size() < 1) {
      return Promise.resolve(null);
    }

    return Workbooks.changeTags(workbooks.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        workbooks,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsWorkbooksSuccess),
          error: standardActionErrorMsg(Messages.changeTagsWorkbooksError),
        },
        (errorItem: IErrorItem<IWorkbookBase>) => WorkbookErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForDatasources(datasources: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (datasources.size() < 1) {
      return Promise.resolve(null);
    }

    return DataSources.changeTags(datasources.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        datasources,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsDatasourcesSuccess),
          error: standardActionErrorMsg(Messages.changeTagsDatasourcesError),
        },
        (errorItem: IErrorItem<IDataSourceBase>) => DataSourceErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForMetrics(metrics: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (metrics.size() < 1) {
      return Promise.resolve(null);
    }

    return Metrics.changeTags(metrics.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        metrics,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsMetricsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsMetricsError),
        },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForPublishedConnections(publishedConnections: Set<IContentInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    if (publishedConnections.size() < 1) {
      return Promise.resolve(null);
    }

    return PublishedConnections.changeTags(publishedConnections.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        publishedConnections,
        result,
        {
          success: standardActionSuccessMsg(Messages.changeTagsPublishedConnectionsSuccess),
          error: standardActionErrorMsg(Messages.changeTagsPublishedConnectionsError),
        },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem)
      );
    });
  }

  function changeTagsForDatabases(databases: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const databasesArray = databases.toArray();
    if (databasesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return Databases.changeTags({ databases: databasesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsDatabasesSuccess({
            ITEM_COUNT: databasesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(databasesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsDatabasesError({
            ITEM_COUNT: databasesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  function changeTagsForTables(tables: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const tablesArray = tables.toArray();
    if (tablesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return Tables.changeTags({ tables: tablesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsTablesSuccess({
            ITEM_COUNT: tablesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(tablesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsTablesError({
            ITEM_COUNT: tablesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  export function errorMessageFor(errorItem: IErrorItem<any>): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.changeTagsErrorNoPermission();
      case TagErrorCode.TagNameTooLong:
        return Messages.changeTagsErrorNameTooLong();
      case TagErrorCode.TooManyTags:
        return Messages.changeTagsErrorTooManyTags();
      default:
        return Messages.errorUnknown();
    }
  }

}

export type ChangeTagsActionHelperService = typeof ChangeTagsActionHelperService;
